<template>
  <div class="search_input" ref="searchRoot">
    <img src="/search.svg" alt="Search" loading="lazy" class="search_icon" />

    <input
      type="text"
      id="search"
      name="search"
      :placeholder="$t('search')"
      ref="searchInput"
      v-model="query"
      @input="onInput"
    />

    <Transition name="search_results">
      <div class="search_results" v-if="viewResults">
        <li v-if="results.length === 0" class="search_not_found">
          <p>{{ $t("searchError") }}</p>
        </li>

        <li
          class="search_result"
          v-for="user in results"
          :key="user.id"
          @click="goToProfile(user.id)"
        >
          <div class="search_results_user">
            <div class="search_results_avatar">
              <Avatar :user="user" />
            </div>

            <div class="search_results_info">
              <p v-if="user.name">{{ user.name }}</p>
              <span v-if="user.username">@{{ user.username }}</span>
            </div>
          </div>

          <FollowBtn
            @click.stop
            :userId="user.id"
            :isFollowing="user.i_follow_him"
            class="search_follow_btn"
          />
        </li>
      </div>
    </Transition>
  </div>
</template>

<script>
import { searchUser } from "@/api/axios.js";
import FollowBtn from "@/UI/Button/FollowBtn.vue";
import Avatar from "./Avatar.vue";
import { useClickOutside } from "@/composables/useClickOutside.js";

export default {
  name: "Search",
  components: { FollowBtn, Avatar },
  data() {
    return {
      query: "",
      results: [],
      inactiveTimeout: null,
      timeout: null,
      viewResults: false,
      removeClickOutside: null,
    };
  },
  emits: ["close"],
  methods: {
    async onInput() {
      this.timeout = setTimeout(async () => {
        if (this.query.trim().length === 0) {
          this.results = [];
          return;
        }

        try {
          const resp = await searchUser(this.query);
          this.results = resp.data.results || [];
          this.viewResults = true;
        } catch (e) {
          console.error("Search error:", e);
        }
      }, 400);
    },
    goToProfile(id) {
      window.location.href = `/profile/${id}`;
    },
    handleScroll() {
      if (window.scrollY > 60) {
        this.viewResults = false;
        this.$refs.searchInput.blur();
      }
    },
  },
  mounted() {
    this.removeClickOutside = useClickOutside(
      this.$el,
      () => (this.viewResults = false),
    );
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeUnmount() {
    this.removeClickOutside?.();
    window.removeEventListener("scroll", this.handleScroll);
  },
};
</script>

<style>
.search_input {
  position: relative;
  width: min(100%, 657px);
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.search_results_user {
  display: flex;
  align-items: center;
}

.search_input input {
  padding: 8px 0 8px 38px;
  font-size: 15px;
  box-sizing: border-box;
  border-radius: 12px;
  background: var(--edit-profile-form);
  color: var(--text-color);
  width: 70%;
}

.search_icon {
  width: 16px;
  height: 16px;
  position: absolute;
  top: 8px;
  left: 10px;
}

.search_results {
  position: absolute;
  top: 40px;
  left: 0;
  max-height: 250px;
  max-width: 500px;
  width: 70%;
  border: 1px solid var(--border-color);
  background-color: var(--search-result-bg);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(30px);
  border-radius: 15px;
  overflow-y: auto;
  z-index: 10;
}

.search_results::-webkit-scrollbar {
  width: 5px;
}

.search_results::-webkit-scrollbar-track {
  margin: 8px 0;
}

.search_results::-webkit-scrollbar-thumb {
  background-color: rgba(104, 104, 104, 0.4);
  border-radius: 20px;
  margin: 10px 0;
}

.search_results::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.search_result,
.search_not_found {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  text-align: left;
  background-color: transparent;
  transition: background 0.2s ease;
}

.search_not_found p {
  width: 100%;
  text-align: center;
  font-size: 15px;
  color: var(--description-color);
}

.search_result:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.search_results_avatar,
.search_results_avatar img {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-right: 10px;
}

.search_results_info p {
  color: var(--text-color);
  font-weight: 600;
  font-size: 12px;
}

.search_results_info span {
  color: var(--description-color);
  font-size: 12px;
}

.search_follow_btn {
  background: var(--main-color);
  font-weight: 700;
  padding: 8px 20px;
  font-size: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.search_follow_btn:hover {
  background: var(--main-color-hover);
}

.search_results-enter-active,
.search_results-leave-active {
  transition: all 0.2s ease;
}

.search_results-enter-from,
.search_results-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.search_results-enter-to,
.search_results-leave-from {
  opacity: 1;
  transform: translateY(0px);
}

@media (max-width: 750px) {
  .search_results {
    width: 300px;
  }
}

@media (max-width: 580px) {
  .search_results {
    width: 300px;
    left: -20px;
  }

  .search_input input {
    width: 95%;
  }
}
</style>
